import { useFetch } from "~vue/api";
import { openUrl } from "~vue/utils";
import { CHATSOURCE } from "~vue/utils/constants.js";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, inject, ref } from "vue";

import { useTimedShow } from "./useTimedShow.js";

// Composable to manage feedback state and actions
export const useFeedback = () => {
    const coachingSessionUuid = inject("coachingSessionUuid", null);

    const confirmationButtonCallback = ref(null);
    const questionToDelete = ref(null);
    const feedbackWithReport = ref(null);

    const { execute: fetchFeedback, data: feedbackQuestionHistory } = useFetch("/api/accounts/feedback-questions", { immediate: false }).get().json();

    const goToFeedbackReportFlow = (feedback = null) => {
        if (!coachingSessionUuid) {
            logUserInteraction("feedback_summary_review", { feedback_request_id: feedback?.id });
        } else {
            logUserInteraction("feedback_summary_review", { feedback_request_id: feedback?.id }, coachingSessionUuid);
        }

        if (!feedback && feedbackWithReport.value) {
            feedback = feedbackWithReport.value;
        } else {
            console.error("No feedback found");
        }
        if (feedback.report_cs_id) {
            openUrl(`coach/${feedback.report_cs_id}/`);
            return;
        }

        openUrl("coach/start/?script=feedback_share_results", { queryParams: { chat_source: CHATSOURCE.HOME_USER_FEEDBACK, fq_id: feedback.id } });
    };

    const showConfirmationDialog = ref(false);
    const deleteUrl = computed(() => `/api/accounts/feedback-question/${questionToDelete.value}`);
    const { execute: deleteFeedbackQuestion } = useFetch(deleteUrl, { immediate: false }).delete();

    const confirmationDialogTitle = ref("");
    const confirmationDialogText = ref("");
    const confirmationButtonText = ref("");

    const confirmDeleteIntentions = (questionId) => {
        showConfirmationDialog.value = true;
        questionToDelete.value = questionId;
        confirmationDialogTitle.value = "Are you sure?";
        confirmationDialogText.value = `Are you sure you want to delete this feedback question? This action cannot be undone.`;
        confirmationButtonText.value = "Delete";
        confirmationButtonCallback.value = deleteQuestion;
    };

    const confirmConcludeAndReviewIntentions = (feedback) => {
        showConfirmationDialog.value = true;
        feedbackWithReport.value = feedback;
        confirmationDialogTitle.value = "Are you sure?";
        confirmationDialogText.value = `Concluding will mean you can no longer collect responses on this question. This cannot be undone.`;
        confirmationButtonText.value = "Conclude & Review";
        confirmationButtonCallback.value = goToFeedbackReportFlow;
    };

    const questionToReopen = ref(null);

    const confirmReopenIntentions = (feedback) => {
        showConfirmationDialog.value = true;
        questionToReopen.value = feedback;
        confirmationDialogTitle.value = "Reopen this feedback request?";
        confirmationDialogText.value = `Reopening will let you share new links and collect more responses. Your previous report will be updated the next time a response comes in.`;
        confirmationButtonText.value = "Reopen";
        confirmationButtonCallback.value = reopenQuestion;
    };

    const reopenUrl = computed(() => `/api/accounts/feedback-question/${questionToReopen.value?.id}/reopen`);
    const { execute: reopenFeedbackQuestion } = useFetch(reopenUrl, { immediate: false }).post();

    const reopenQuestion = async () => {
        if (!questionToReopen.value) {
            return;
        }
        logUserInteraction("feedback_request_reopened", { feedback_request_id: questionToReopen.value.id }, coachingSessionUuid);
        await reopenFeedbackQuestion();
        questionToReopen.value = null;
        showConfirmationDialog.value = false;
        await fetchFeedback();
    };

    const deleteQuestion = async () => {
        await deleteFeedbackQuestion();
        questionToDelete.value = null; // Reset after deletion
        showConfirmationDialog.value = false;
        fetchFeedback();
    };

    const { show: showCopied, isShowing: buttonClicked } = useTimedShow({ duration: 1500 });
    const { show: showUrlCopied, isShowing: urlButtonClicked } = useTimedShow({ duration: 1500 });
    const feedbackReviewLink = computed(() => feedbackReview.value?.url);
    const questionToGenerateLinkFor = ref(null);
    const generatedQuestionLinks = ref({});

    const { execute: generateFeedbackLink, data: feedbackReview } = useFetch("/api/accounts/feedback-url", { immediate: false })
        .post(computed(() => ({ question_id: questionToGenerateLinkFor.value })))
        .json();

    const copyLink = async (questionId, context = "list") => {
        const logCopied = () => {
            logUserInteraction(
                "feedback_request_link_copied",
                {
                    feedback_request_id: questionId,
                    copy_location: context,
                },
                coachingSessionUuid,
            );
        };

        const showManualCopyDialog = (url) => {
            confirmationDialogTitle.value = "Copy this link:";
            confirmationDialogText.value = url;
            confirmationButtonText.value = "Got it!";
            confirmationButtonCallback.value = () => {
                showConfirmationDialog.value = false;
                // Assume they copied the link if they clicked "Got it" instead of "Cancel"
                logCopied();
            };
            showConfirmationDialog.value = true;
        };

        const performCopy = async (url) => {
            // Ensure question history is loaded so we can include the question text in the message
            if (!feedbackQuestionHistory.value?.find((q) => q.id === questionId)) {
                await fetchFeedback();
            }
            const questionText = feedbackQuestionHistory.value?.find((q) => q.id === questionId)?.question;
            const message = questionText
                ? `Hi, I'd love to get your anonymous perspective on: "${questionText}". It only takes a few minutes: ${url}`
                : `Hi, I'm gathering anonymous feedback and would value your perspective. It only takes a few minutes: ${url}`;
            try {
                await navigator.clipboard.writeText(message);
            } catch {
                // We couldn't use the clipboard API, so show the user the link to copy manually
                showManualCopyDialog(message);
                logUserInteraction("feedback_request_link_copy_error", { error: "Could not write to the clipboard; showed manual copy dialog" }, coachingSessionUuid);
                return;
            }

            showCopied();
            logCopied();
        };

        if (generatedQuestionLinks.value[questionId]) {
            await performCopy(generatedQuestionLinks.value[questionId]);
            return;
        }

        questionToGenerateLinkFor.value = questionId;
        await generateFeedbackLink();

        // Store the generated link in our tracking object
        if (feedbackReviewLink.value) {
            generatedQuestionLinks.value[questionId] = feedbackReviewLink.value;
            await performCopy(feedbackReviewLink.value);
        }
        questionToGenerateLinkFor.value = null;
    };

    const copyUrl = async (questionId, context = "list") => {
        const logCopied = () => {
            logUserInteraction(
                "feedback_request_url_copied",
                {
                    feedback_request_id: questionId,
                    copy_location: context,
                },
                coachingSessionUuid,
            );
        };

        const showManualCopyDialog = (url) => {
            confirmationDialogTitle.value = "Copy this URL:";
            confirmationDialogText.value = url;
            confirmationButtonText.value = "Got it!";
            confirmationButtonCallback.value = () => {
                showConfirmationDialog.value = false;
                logCopied();
            };
            showConfirmationDialog.value = true;
        };

        const performCopy = async (url) => {
            try {
                await navigator.clipboard.writeText(url);
            } catch {
                showManualCopyDialog(url);
                logUserInteraction("feedback_request_url_copy_error", { error: "Could not write to the clipboard; showed manual copy dialog" }, coachingSessionUuid);
                return;
            }

            showUrlCopied();
            logCopied();
        };

        if (generatedQuestionLinks.value[questionId]) {
            await performCopy(generatedQuestionLinks.value[questionId]);
            return;
        }

        questionToGenerateLinkFor.value = questionId;
        await generateFeedbackLink();

        if (feedbackReviewLink.value) {
            generatedQuestionLinks.value[questionId] = feedbackReviewLink.value;
            await performCopy(feedbackReviewLink.value);
        }
        questionToGenerateLinkFor.value = null;
    };

    return {
        confirmationDialogTitle,
        confirmationDialogText,
        confirmationButtonText,
        showConfirmationDialog,
        confirmationButtonCallback,
        goToFeedbackReportFlow,
        confirmDeleteIntentions,
        confirmConcludeAndReviewIntentions,
        confirmReopenIntentions,
        feedbackQuestionHistory,
        fetchFeedback,
        copyLink,
        buttonClicked,
        copyUrl,
        urlButtonClicked,
    };
};
